import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, Share, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * `beforeinstallprompt` is a Window event not yet in the standard TS lib —
 * declare a minimal interface for the access site.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa.installPromptDismissed';

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  // iPad on iOS 13+ reports MacIntel but has touch — handle both.
  const isIOSUserAgent = /iphone|ipod|ipad/.test(ua);
  const isMacWithTouch =
    /macintosh/.test(ua) &&
    typeof document !== 'undefined' &&
    'ontouchend' in document;
  return isIOSUserAgent || isMacWithTouch;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

/**
 * Floating, dismissable "Install app" prompt mounted once at the App root.
 *
 * On Chromium: captures `beforeinstallprompt`, renders a small bottom-right
 * card with an Install button that fires `prompt()` on click. Auto-hides on
 * `appinstalled`.
 *
 * On iOS Safari: shows the same card, but tapping Install opens a tiny
 * instructions sheet (iOS doesn't expose a programmatic install API).
 *
 * Hides itself entirely when:
 *   - the app is already running standalone (display-mode: standalone, or
 *     the iOS `navigator.standalone === true` quirk),
 *   - the user has dismissed it (per-browser, via localStorage),
 *   - the current route is the reader (full-screen UI; don't obstruct it).
 *
 * Implementation note: a header button would be cleaner but the shared
 * Layout component is owned outside this worker's scope. A floating card
 * keeps the PWA story working without touching shared chrome.
 */
export function InstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosOpen, setIosOpen] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  });
  const location = useLocation();
  const inReader = /\/source\/[^/]+\/comic\/[^/]+\/read(\/|$)/.test(location.pathname);

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setPrompt(null);
      setDismissed(true);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (prompt) {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') setPrompt(null);
    } else if (detectIOS()) {
      setIosOpen(true);
    }
  }, [prompt]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode etc. */
    }
  }, []);

  if (dismissed || inReader) return null;
  if (isStandalone()) return null;
  const showButton = prompt !== null || detectIOS();
  if (!showButton) return null;

  return (
    <>
      <div
        role="dialog"
        aria-label="Install Bunko"
        className="fixed bottom-4 right-4 z-30 flex items-center gap-2 rounded-lg border border-border bg-card/95 p-2 pl-3 text-sm shadow-lg backdrop-blur"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">Install Bunko</span>
        <Button size="sm" onClick={handleInstall}>
          Install
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="h-7 w-7"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {iosOpen && <IOSInstallSheet onClose={() => setIosOpen(false)} />}
    </>
  );
}

function IOSInstallSheet({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
      aria-label="Close install instructions"
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg"
      >
        <div className="mb-2 flex items-center gap-2">
          <Share className="h-4 w-4" />
          <h2 className="text-sm font-semibold">Install on iOS</h2>
        </div>
        <ol className="space-y-1.5 text-xs text-muted-foreground">
          <li>
            1. Tap the <strong>Share</strong> icon in Safari&apos;s toolbar.
          </li>
          <li>
            2. Scroll down and pick <strong>&ldquo;Add to Home Screen&rdquo;</strong>.
          </li>
          <li>
            3. Confirm the name and tap <strong>Add</strong>.
          </li>
        </ol>
        <p className="mt-3 text-[10px] text-muted-foreground">
          Tap anywhere outside this box to close.
        </p>
      </div>
    </button>
  );
}
